// /insights — funnel + response-rate cuts.
//
// Session 7 PR1. Single GET /api/insights round-trip; the worker caches the
// aggregate for 5 min in KV and "Refresh" busts that. No charting library:
// the funnel is a stacked bar built from CSS percentages, and each cut is
// a small per-bucket card with rate + count.

import { Link } from "react-router";
import { RefreshCw } from "lucide-react";
import {
  useInsights,
  useRefreshInsights,
  type Cadence,
  type InsightsAggregate,
  type WaitingStat,
  type RateBucket,
  type UnderperformingCut,
} from "../lib/insights";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Pill } from "../components/ui/Pill";
import { EmptyState } from "../components/ui/EmptyState";
import { Skeleton } from "../components/ui/Skeleton";
import { useState } from "react";

const FUNNEL_ORDER: Array<{
  key: keyof InsightsAggregate["funnel"];
  label: string;
  color: string;
}> = [
  { key: "interested", label: "Interested", color: "bg-neutral-700" },
  { key: "preparing", label: "Preparing", color: "bg-neutral-600" },
  { key: "approved", label: "Approved", color: "bg-accent-500/40" },
  { key: "applied", label: "Applied", color: "bg-accent-500/70" },
  { key: "interviewing", label: "Interviewing", color: "bg-accent-400" },
  { key: "offer", label: "Offer", color: "bg-success-fg" },
  { key: "rejected", label: "Rejected", color: "bg-danger-fg/70" },
  { key: "withdrawn", label: "Withdrawn", color: "bg-neutral-800" },
];

const AXIS_LABELS: Record<string, string> = {
  responsibilities: "Responsibilities",
  seniority: "Seniority",
  domain: "Domain",
  tech_stack: "Tech stack",
  location_fit: "Location fit",
};

export default function Insights() {
  const insights = useInsights();
  const refresh = useRefreshInsights();
  const [refreshing, setRefreshing] = useState(false);

  async function onRefresh() {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Insights</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Funnel and response-rate cuts across your applications. Cached
            5 min server-side; refresh after a status change to recompute.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onRefresh}
          loading={refreshing}
          leadingIcon={<RefreshCw size={13} aria-hidden />}
        >
          Refresh
        </Button>
      </header>

      {insights.isLoading ? (
        <InsightsSkeleton />
      ) : insights.error ? (
        <EmptyState
          tone="danger"
          title="Couldn't load insights"
          description={String(insights.error)}
          action={
            <Button variant="secondary" onClick={() => insights.refetch()}>
              Retry
            </Button>
          }
        />
      ) : !insights.data || insights.data.funnel.total === 0 ? (
        <EmptyState
          title="No applications yet"
          description="Insights light up once you've tracked a few applications and they've moved through the funnel."
          action={
            <Button
              variant="secondary"
              onClick={() => {
                window.location.href = "/applications";
              }}
            >
              Go to applications
            </Button>
          }
        />
      ) : (
        <InsightsContent data={insights.data} />
      )}
    </section>
  );
}

function InsightsContent({ data }: { data: InsightsAggregate }) {
  return (
    <>
      <Headline
        overall={data.overall}
        total={data.funnel.total}
        stuckCount={data.stuck_count ?? 0}
        progressedCount={data.progressed_count ?? 0}
      />
      {data.cadence ? <CadenceCard cadence={data.cadence} /> : null}

      <FunnelCard funnel={data.funnel} />

      {data.underperforming && data.underperforming.length > 0 ? (
        <UnderperformingPanel
          cuts={data.underperforming}
          overallRate={data.overall.rate}
        />
      ) : null}

      <CutGrid
        title="Response rate by composite fit score"
        description="Of applications that have been submitted, the share that reached interviewing+. Score buckets follow the same banding as /jobs."
        buckets={data.by_score_band}
        emptyHint="No submitted applications with scored postings yet."
      />

      <PerAxisGrid by_axis={data.by_axis} />

      <CutGrid
        title="Response rate by ATS platform"
        description="Greenhouse vs. Workday vs. Ashby — narrow signal on application UX often shows up here."
        buckets={data.by_ats}
        emptyHint="No catalog applications have been submitted."
      />

      <CutGrid
        title="Response rate by company tier"
        description="Tier 1/2/3 from the catalog. Interview rate is usually highest for the tier you have the strongest existing context in."
        buckets={data.by_tier}
        emptyHint="No catalog applications have been submitted."
      />
    </>
  );
}

// Learning-from-failure surface: cuts whose interview rate lags the overall
// rate enough, on enough volume, to suggest where to stop spending effort.
function UnderperformingPanel({
  cuts,
  overallRate,
}: {
  cuts: UnderperformingCut[];
  overallRate: number;
}) {
  const overallPct = Math.round(overallRate * 1000) / 10;
  return (
    <Card>
      <CardHeader
        title="Where your effort is underperforming"
        description={`These cuts reach interviews well below your overall ${overallPct}% rate, on enough volume to act on — consider redirecting effort away from them.`}
      />
      <CardBody>
        <ul className="space-y-2">
          {cuts.map((c) => {
            const cutPct = Math.round(c.rate * 1000) / 10;
            const deltaPts = Math.round(c.delta * 1000) / 10;
            return (
              <li
                key={`${c.dimension}:${c.key}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning-border bg-warning-bg/40 px-3 py-2"
              >
                <span className="min-w-0 text-sm text-neutral-100">
                  <span className="text-neutral-400">{c.dimension}:</span>{" "}
                  <span className="font-medium">{c.label}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400">
                    {c.responded}/{c.submitted} → {cutPct}%
                  </span>
                  <Pill tone="warning">{deltaPts} pts</Pill>
                </span>
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}

function Headline({
  overall,
  total,
  stuckCount,
  progressedCount,
}: {
  overall: InsightsAggregate["overall"];
  total: number;
  stuckCount: number;
  progressedCount: number;
}) {
  const ratePct = Math.round(overall.rate * 1000) / 10;
  return (
    <Card>
      <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat
          label="Tracked applications"
          value={total.toLocaleString()}
          sub={
            stuckCount > 0
              ? `${stuckCount} stuck · ${progressedCount} reached interviewing`
              : `${progressedCount} reached interviewing`
          }
        />
        <Stat
          label="Submitted"
          value={overall.submitted.toLocaleString()}
          sub="sent: applied onward, plus withdrawals after sending"
        />
        <Stat
          label="Interview rate"
          value={overall.submitted === 0 ? "—" : `${ratePct}%`}
          sub={`${overall.responded} of ${overall.submitted} submitted reached an interview`}
        />
      </CardBody>
    </Card>
  );
}

// Submission cadence (SUBMISSION_FLOW_SPRINTS PR E). The hunt's bottleneck is
// sending, so this sits above the funnel: submissions per week against the
// floor, warm touches alongside, and how long packets wait on you.
function CadenceCard({ cadence }: { cadence: Cadence }) {
  const max = Math.max(cadence.target_per_week, ...cadence.weeks.map((w) => w.submissions));
  const floorPct = (cadence.floor_per_week / max) * 100;
  const weekLabel = (ts: number) =>
    new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  const wait = (w: WaitingStat) =>
    w.count === 0 ? "none waiting" : `${w.count} · median ${w.median_days}d · oldest ${w.max_days}d`;
  return (
    <Card>
      <CardHeader
        title="Submission cadence"
        description={`Goal: ${cadence.target_per_week} a week. Targeting work waits until you hold ${cadence.floor_per_week}+ a week for a month.`}
      />
      <CardBody className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat
            label="Submitted this week"
            value={`${cadence.this_week.submissions} / ${cadence.target_per_week}`}
            sub="Weeks start Monday (UTC)"
          />
          <Stat
            label="Warm touches this week"
            value={String(cadence.this_week.warm_touches)}
            sub="Messages sent to contacts"
          />
          <Stat
            label={`Weeks at ${cadence.floor_per_week}+`}
            value={`${Math.min(cadence.floor_streak_weeks, 4)} / 4`}
            sub="Completed weeks in a row"
          />
        </div>

        <div>
          <div className="relative flex h-28 items-end gap-2 border-b border-neutral-700">
            <div
              className="pointer-events-none absolute inset-x-0 border-t border-dashed border-neutral-500"
              style={{ bottom: `${floorPct}%` }}
              aria-hidden
            >
              <span className="absolute -top-4 right-0 text-[10px] text-neutral-500">
                floor {cadence.floor_per_week}
              </span>
            </div>
            {cadence.weeks.map((w) => (
              <div
                key={w.week_start}
                className="group flex h-full flex-1 items-end"
                title={`Week of ${weekLabel(w.week_start)}: ${w.submissions} submitted, ${w.warm_touches} warm touches`}
              >
                <div
                  className="w-full rounded-t bg-accent-500 group-hover:bg-accent-400"
                  style={{ height: `${(w.submissions / max) * 100}%`, minHeight: w.submissions ? 4 : 0 }}
                />
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-2 text-[10px] text-neutral-500">
            {cadence.weeks.map((w) => (
              <span key={w.week_start} className="flex-1 truncate text-center tabular-nums">
                {weekLabel(w.week_start)}
              </span>
            ))}
          </div>
          <div className="mt-1 flex gap-2 text-[10px] text-neutral-400">
            {cadence.weeks.map((w) => (
              <span key={w.week_start} className="flex-1 text-center tabular-nums">
                {w.submissions}
                <span className="text-neutral-600"> · {w.warm_touches}</span>
              </span>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-neutral-500">
            Bars: submissions per week. Numbers: submitted · warm touches.
          </p>
        </div>

        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-neutral-500">Waiting in preparing</dt>
            <dd className="text-neutral-200">{wait(cadence.waiting.preparing)}</dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-500">Approved, not sent</dt>
            <dd className="text-neutral-200">{wait(cadence.waiting.approved)}</dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-500">Approval to submit</dt>
            <dd className="text-neutral-200">
              {cadence.approval_to_submit.median_hours === null
                ? "no submissions through /review yet"
                : `median ${cadence.approval_to_submit.median_hours}h (${cadence.approval_to_submit.n})`}
            </dd>
          </div>
        </dl>
      </CardBody>
    </Card>
  );
}

function FunnelCard({ funnel }: { funnel: InsightsAggregate["funnel"] }) {
  const max = funnel.total || 1;
  return (
    <Card>
      <CardHeader
        title="Funnel"
        description="Stacked share of every tracked application by current stage."
      />
      <CardBody className="space-y-3">
        <div className="flex h-3 w-full overflow-hidden rounded bg-neutral-900">
          {FUNNEL_ORDER.map(({ key, color }) => {
            const v = funnel[key];
            if (v === 0) return null;
            const pct = (v / max) * 100;
            return (
              <div
                key={key}
                className={color}
                style={{ width: `${pct}%` }}
                aria-label={`${key}: ${v}`}
              />
            );
          })}
        </div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
          {FUNNEL_ORDER.map(({ key, label, color }) => (
            <li key={key} className="flex items-center gap-2">
              <span className={`inline-block h-2 w-2 rounded ${color}`} />
              <Link
                to={`/applications?status=${key}`}
                className="text-neutral-300 hover:text-neutral-100"
              >
                <span className="font-medium tabular-nums text-neutral-100">
                  {funnel[key]}
                </span>{" "}
                <span className="text-neutral-500">{label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function CutGrid({
  title,
  description,
  buckets,
  emptyHint,
}: {
  title: string;
  description: string;
  buckets: RateBucket[];
  emptyHint: string;
}) {
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <CardBody>
        {buckets.length === 0 ? (
          <p className="text-xs text-neutral-500">{emptyHint}</p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {buckets.map((b) => (
              <BucketCard key={b.key} bucket={b} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function PerAxisGrid({
  by_axis,
}: {
  by_axis: InsightsAggregate["by_axis"];
}) {
  return (
    <Card>
      <CardHeader
        title="Response rate by score axis"
        description="Each axis bucketed low (1-4) / mid (5-7) / high (8-10). When a bucket's rate is much higher than the others, that's the axis the user should optimize for."
      />
      <CardBody>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {by_axis.map(({ axis, buckets }) => (
            <div key={axis} className="space-y-2">
              <h4 className="text-[11px] uppercase tracking-wider text-neutral-500">
                {AXIS_LABELS[axis] ?? axis}
              </h4>
              {buckets.length === 0 ? (
                <p className="text-xs text-neutral-500">No signal yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {buckets.map((b) => (
                    <BucketRow key={b.key} bucket={b} />
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function BucketCard({ bucket }: { bucket: RateBucket }) {
  const ratePct = Math.round(bucket.rate * 1000) / 10;
  return (
    <li className="rounded border border-neutral-800 bg-neutral-950/40 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-neutral-200">
          {bucket.label}
        </span>
        <span className="font-mono text-sm text-neutral-100">
          {bucket.submitted === 0 ? "—" : `${ratePct}%`}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-neutral-500">
        {bucket.responded} / {bucket.submitted} submitted reached an interview
      </p>
    </li>
  );
}

function BucketRow({ bucket }: { bucket: RateBucket }) {
  const ratePct = Math.round(bucket.rate * 1000) / 10;
  return (
    <li className="flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-950/30 px-2 py-1.5 text-[11px]">
      <span className="text-neutral-300">{bucket.label}</span>
      <span className="flex items-center gap-2">
        <span className="text-neutral-500">
          {bucket.responded}/{bucket.submitted}
        </span>
        <Pill
          tone={
            bucket.submitted === 0
              ? "neutral"
              : bucket.rate >= 0.25
                ? "success"
                : bucket.rate >= 0.1
                  ? "info"
                  : "neutral"
          }
          className="font-mono"
        >
          {bucket.submitted === 0 ? "—" : `${ratePct}%`}
        </Pill>
      </span>
    </li>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">
        {label}
      </p>
      <p className="mt-1 text-3xl font-semibold tabular-nums text-neutral-100">
        {value}
      </p>
      <p className="mt-1 text-xs text-neutral-500">{sub}</p>
    </div>
  );
}

function InsightsSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-2 h-7 w-20" />
              <Skeleton className="mt-1 h-3 w-32" />
            </div>
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-3 h-3 w-full" />
          <Skeleton className="mt-3 h-3 w-3/4" />
        </CardBody>
      </Card>
    </div>
  );
}
